import { useEffect, useRef, useState } from 'react';

const DEFAULT_ROOT_MARGIN = '300px';

/**
 * Defer assigning an image URL until the element is near the viewport.
 * Returns a ref to attach to the placeholder/img wrapper and whether loading is allowed.
 */
export function useLazyImageSrc({ enabled = true, rootMargin = DEFAULT_ROOT_MARGIN } = {}) {
  const ref = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(!enabled);

  useEffect(() => {
    if (!enabled) {
      setShouldLoad(true);
      return undefined;
    }

    const element = ref.current;
    if (!element) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoad(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [enabled, rootMargin]);

  return { ref, shouldLoad };
}
